import React, { useState, useEffect } from 'react';
import { Modal, Spin, Icon, Button, message } from 'antd';
import { platformApi } from '../api';
import { io } from 'socket.io-client';
import { ENDPOINT } from '../constants';

const PymidolSyncModal = ({ visible, onClose, moduleType, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('idle'); // 'idle' | 'phase1_running' | 'phase2_running' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [syncedInvoices, setSyncedInvoices] = useState([]);

  const getUserIdFromToken = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window.atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload).id;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    if (!visible) return;

    // Connect to backend socket
    const socket = io(ENDPOINT, {
      transports: ['websocket'],
      auth: { token: localStorage.getItem('token') },
    });

    socket.on('connect', () => {
      console.log('✅ Connected to Pymidol Sync progress socket');
    });

    socket.on('pymidol_progress', (data) => {
      const currentUserId = getUserIdFromToken();
      if (data && data.userId === currentUserId) {
        console.log('📡 Received Pymidol sync progress update:', data);
        if (data.status) {
          if (data.status === 'catalog_validation_failed') {
            setStatus('error');
            setErrorMsg(data.message || 'Vehicle/Model/Color does not exist in DB.');
          } else {
            setStatus(data.status);
          }
        }
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [visible]);

  useEffect(() => {
    if (visible) {
      handleStartSync();
    } else {
      // Reset state on close
      setLoading(false);
      setStatus('idle');
      setErrorMsg('');
      setSyncedInvoices([]);
    }
  }, [visible]);

  const handleStartSync = () => {
    setLoading(true);
    setStatus('phase1_running');
    setErrorMsg('');
    setSyncedInvoices([]);

    console.log(`Starting single-flow Pymidol sync for module: ${moduleType}`);

    platformApi
      .post('/api/pymidol/pull', { module: moduleType })
      .then((res) => {
        setLoading(false);
        const { data } = res;
        if (data && data.code === 200) {
          const resp = data.response || {};

          // Check if response indicates validation error or failure
          if (resp.success === false || resp.validationError) {
            setStatus('error');
            setErrorMsg(resp.message || 'Automation failed to complete due to validation errors.');
            return;
          }

          setStatus('success');
          const insertedList = resp.inserted || [];
          const skippedList = resp.skipped || [];
          const totalSynced = [...insertedList, ...skippedList];
          setSyncedInvoices(totalSynced);

          if (totalSynced.length > 0) {
            message.success('Pending purchase invoices pulled and saved successfully.');
          } else {
            message.info('No pending purchase invoices found in Pymidol.');
          }
          if (onSuccess) {
            onSuccess();
          }
          setTimeout(() => {
            onClose();
          }, 4500); // 4.5 seconds to allow reading the list
        } else {
          setStatus('error');
          setErrorMsg(data?.response?.message || 'Automation failed to complete.');
        }
      })
      .catch((err) => {
        setLoading(false);
        setStatus('error');
        console.error('Pymidol sync error:', err);
        const errMsg = err?.response?.data?.message || err?.message || 'Connection to automation server failed.';
        setErrorMsg(errMsg);
      });
  };

  const getStatusContent = () => {
    const loadingIcon = <Icon type="loading" style={{ fontSize: 48, color: '#1890ff' }} spin />;
    const moduleName = moduleType === 'vehicle_purchase' ? 'vehicle purchase' : 'spare purchase';

    switch (status) {
      case 'phase1_running':
        return (
          <div style={{ textAlign: 'center', padding: '30px 0' }}>
            <Spin indicator={loadingIcon} />
            <h3 style={{ marginTop: 25, color: '#1890ff', fontSize: '22px', fontWeight: '600' }}>Phase 1: Syncing Invoices</h3>
            <p style={{ color: '#333', fontSize: '16px', maxWidth: '480px', margin: '15px auto 0', lineHeight: '1.6' }}>
              Pulling pending invoices from Pymidol and inserting them into the database...
            </p>
          </div>
        );
      case 'phase2_running':
        return (
          <div style={{ textAlign: 'center', padding: '30px 0' }}>
            <Spin indicator={loadingIcon} />
            <h3 style={{ marginTop: 25, color: '#2f54eb', fontSize: '22px', fontWeight: '600' }}>Phase 2: Saving in Pymidol</h3>
            <p style={{ color: '#333', fontSize: '16px', maxWidth: '480px', margin: '15px auto 0', lineHeight: '1.6' }}>
              Saving the successfully synchronized purchases back to the Pymidol portal...
            </p>
            <p style={{ color: '#888', fontSize: '14px', marginTop: 20 }}>
              Completing final automation steps. Please wait.
            </p>
          </div>
        );
      case 'success': {
        const hasSynced = syncedInvoices.length > 0;
        return (
          <div style={{ textAlign: 'center', padding: '30px 0' }}>
            {hasSynced ? (
              <>
                <Icon type="check-circle" theme="twoTone" twoToneColor="#52c41a" style={{ fontSize: 60 }} />
                <h3 style={{ marginTop: 25, color: '#52c41a', fontSize: '24px', fontWeight: '600' }}>Process Completed</h3>
                <p style={{ color: '#333', fontSize: '16px', marginTop: 15, lineHeight: '1.6', maxWidth: '480px', marginLeft: 'auto', marginRight: 'auto' }}>
                  All pending {moduleName} invoices have been successfully pulled, synchronized, and saved.
                </p>
                <div style={{ marginTop: 25, padding: '15px 20px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '6px', display: 'inline-block', textAlign: 'left', minWidth: '280px' }}>
                  <strong style={{ color: '#389e0d', display: 'block', marginBottom: 8, fontSize: '15px' }}>Processed Invoices:</strong>
                  <ul style={{ margin: 0, paddingLeft: 20, color: '#555', fontSize: '14px', lineHeight: '1.8' }}>
                    {syncedInvoices.map((inv, idx) => (
                      <li key={idx} style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{inv}</li>
                    ))}
                  </ul>
                </div>
              </>
            ) : (
              <>
                <Icon type="info-circle" theme="twoTone" twoToneColor="#1890ff" style={{ fontSize: 60 }} />
                <h3 style={{ marginTop: 25, color: '#1890ff', fontSize: '24px', fontWeight: '600' }}>No Pending Records in Pymidol</h3>
                <p style={{ color: '#333', fontSize: '16px', marginTop: 15, lineHeight: '1.6', maxWidth: '480px', marginLeft: 'auto', marginRight: 'auto' }}>
                  There are currently no pending {moduleName} invoices available for synchronization in the Pymidol portal.
                </p>
              </>
            )}
          </div>
        );
      }
      case 'error':
        return (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Icon type="close-circle" theme="twoTone" twoToneColor="#f5222d" style={{ fontSize: 60 }} />
            <h3 style={{ marginTop: 20, color: '#f5222d', fontSize: '24px', fontWeight: '600' }}>Automation Failed</h3>
            <div style={{
              margin: '15px auto 10px',
              padding: '16px 20px',
              backgroundColor: '#ffffff',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              textAlign: 'left',
              maxWidth: '520px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
            }}>
              <p style={{
                color: '#262626',
                fontSize: '14px',
                margin: 0,
                lineHeight: '1.6',
                whiteSpace: 'pre-line',
                fontFamily: 'inherit'
              }}>
                {errorMsg}
              </p>
            </div>
            <Button type="primary" onClick={handleStartSync} style={{ marginTop: 15, fontSize: '15px', height: '38px' }}>
              Retry Sync
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  const isRunning = status === 'phase1_running' || status === 'phase2_running';

  return (
    <Modal
      title={
        <span style={{ fontSize: '18px', fontWeight: '600' }}>
          <Icon type="sync" spin={isRunning} style={{ marginRight: 10, color: '#1890ff', fontSize: '18px' }} />
          Pymidol Integration
        </span>
      }
      visible={visible}
      onCancel={isRunning ? null : onClose}
      footer={
        isRunning ? null : (
          <Button onClick={onClose} type="default" style={{ fontSize: '15px', height: '36px' }}>
            Close
          </Button>
        )
      }
      closable={!isRunning}
      maskClosable={!isRunning}
      destroyOnClose
      centered
      width={600}
    >
      {getStatusContent()}
    </Modal>
  );
};

export default PymidolSyncModal;
